import { useMemo, useRef, useEffect } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';

const BASE_HEX_SIZE = 0.00045; // degrees ≈ 50m at mid-latitudes
const SQRT3 = Math.sqrt(3);
const HEX_RADIUS_M = 50;

const FOG_COLOR = 'rgba(240, 240, 240, 0.82)';
const GRID_COLOR = 'rgba(190, 190, 190, 0.25)';
const GRID_MIN_ZOOM = 12;
const EXPLORED_COLOR = '#5B7EA4';
const EXPLORED_RGB = '91,126,164';

const HEX_ANGLES = Array.from({ length: 6 }, (_, i) => (Math.PI / 180) * (60 * i));

const ZOOM_TIERS = [
  { minZoom: 14, scale: 1 },
  { minZoom: 12, scale: 4 },
  { minZoom: 10, scale: 16 },
  { minZoom: 8, scale: 64 },
  { minZoom: 6, scale: 256 },
  { minZoom: 4, scale: 1024 },
  { minZoom: 3, scale: 4096 },
];

function getTierForZoom(zoom) {
  for (const tier of ZOOM_TIERS) {
    if (zoom >= tier.minZoom) return tier;
  }
  return ZOOM_TIERS[ZOOM_TIERS.length - 1];
}

function toGeoJSON(coord) {
  if (coord.latitude != null) return [coord.longitude, coord.latitude];
  return coord;
}

export function latLngToHex(lat, lng, hexSize = BASE_HEX_SIZE) {
  const q = Math.round(lng / (1.5 * hexSize));
  const isOdd = Math.abs(q % 2) === 1;
  const rowOffset = isOdd ? (SQRT3 / 2) * hexSize : 0;
  const r = Math.round((lat - rowOffset) / (SQRT3 * hexSize));
  return { q, r, hexId: `${q}_${r}` };
}

export function hexCenter(q, r, hexSize = BASE_HEX_SIZE) {
  const isOdd = Math.abs(q % 2) === 1;
  const lng = q * 1.5 * hexSize;
  const lat = r * SQRT3 * hexSize + (isOdd ? (SQRT3 / 2) * hexSize : 0);
  return { lat, lng };
}

export function buildExploredData(routes) {
  if (!routes || routes.length === 0) {
    return { exploredArea: 0, tiers: new Map() };
  }

  const sorted = [...routes].sort((a, b) => {
    const da = new Date(a.created_at || a.run_date || 0);
    const db = new Date(b.created_at || b.run_date || 0);
    return db - da;
  });

  const allPoints = [];
  for (const route of sorted) {
    const coords = route.route_data?.coordinates;
    if (!coords || coords.length === 0) continue;
    for (const raw of coords) {
      const [lng, lat] = toGeoJSON(raw);
      allPoints.push({ lat, lng });
    }
  }

  const tiers = new Map();

  for (const tier of ZOOM_TIERS) {
    const hexSize = BASE_HEX_SIZE * tier.scale;
    const exploredKeys = new Set();

    for (const { lat, lng } of allPoints) {
      const { hexId } = latLngToHex(lat, lng, hexSize);
      exploredKeys.add(hexId);
    }

    tiers.set(tier.minZoom, { exploredKeys, hexSize });
  }

  const baseTier = tiers.get(ZOOM_TIERS[0].minZoom);
  const hexArea = (3 * SQRT3 / 2) * HEX_RADIUS_M * HEX_RADIUS_M;

  return {
    exploredArea: baseTier ? baseTier.exploredKeys.size * hexArea : 0,
    tiers,
  };
}

function drawHex(ctx, x, y, vo) {
  ctx.moveTo(x + vo[0], y + vo[1]);
  for (let j = 1; j < 6; j++) ctx.lineTo(x + vo[j * 2], y + vo[j * 2 + 1]);
  ctx.closePath();
}

const FogGridLayer = L.GridLayer.extend({
  setTiers(tiers) {
    this._tiers = tiers;
    this.redraw();
  },

  createTile(coords) {
    const tile = document.createElement('canvas');
    const size = this.getTileSize();
    tile.width = size.x;
    tile.height = size.y;
    const ctx = tile.getContext('2d');
    const zoom = coords.z;

    ctx.fillStyle = FOG_COLOR;
    ctx.fillRect(0, 0, size.x, size.y);

    const tier = getTierForZoom(zoom);
    if (!tier) return tile;

    const tierData = this._tiers && this._tiers.get(tier.minZoom);
    const hexSize = BASE_HEX_SIZE * tier.scale;
    const explored = tierData?.exploredKeys;

    const map = this._map;
    const nw = map.unproject([coords.x * size.x, coords.y * size.y], zoom);
    const se = map.unproject([(coords.x + 1) * size.x, (coords.y + 1) * size.y], zoom);
    const ox = coords.x * size.x;
    const oy = coords.y * size.y;

    const midLat = (nw.lat + se.lat) / 2;
    const midLng = (nw.lng + se.lng) / 2;
    const cRef = map.project([midLat, midLng], zoom);
    const vo = [];
    for (let i = 0; i < 6; i++) {
      const vp = map.project([
        midLat + hexSize * Math.sin(HEX_ANGLES[i]),
        midLng + hexSize * Math.cos(HEX_ANGLES[i]),
      ], zoom);
      vo.push(vp.x - cRef.x, vp.y - cRef.y);
    }

    let maxOff = 0;
    for (let i = 0; i < vo.length; i++) maxOff = Math.max(maxOff, Math.abs(vo[i]));
    const pad = maxOff + 5;

    const q0 = Math.floor(nw.lng / (1.5 * hexSize)) - 1;
    const q1 = Math.ceil(se.lng / (1.5 * hexSize)) + 1;
    const r0 = Math.floor(se.lat / (SQRT3 * hexSize)) - 2;
    const r1 = Math.ceil(nw.lat / (SQRT3 * hexSize)) + 2;

    const cutoutXY = [];
    const gridPath = [];
    const showGrid = zoom >= GRID_MIN_ZOOM;

    for (let q = q0; q <= q1; q++) {
      for (let r = r0; r <= r1; r++) {
        const c = hexCenter(q, r, hexSize);
        const pt = map.project([c.lat, c.lng], zoom);
        const x = pt.x - ox;
        const y = pt.y - oy;

        if (x + pad < 0 || x - pad > size.x || y + pad < 0 || y - pad > size.y) continue;

        if (showGrid) gridPath.push(x, y);

        const hexId = `${q}_${r}`;
        if (explored && explored.has(hexId)) {
          cutoutXY.push(x, y);
        }
      }
    }

    if (cutoutXY.length > 0) {
      ctx.globalCompositeOperation = 'destination-out';
      ctx.fillStyle = 'rgba(0,0,0,1)';
      ctx.beginPath();
      for (let i = 0; i < cutoutXY.length; i += 2) {
        drawHex(ctx, cutoutXY[i], cutoutXY[i + 1], vo);
      }
      ctx.fill();

      ctx.globalCompositeOperation = 'source-over';
      ctx.fillStyle = `rgba(${EXPLORED_RGB},0.42)`;
      ctx.beginPath();
      for (let i = 0; i < cutoutXY.length; i += 2) {
        drawHex(ctx, cutoutXY[i], cutoutXY[i + 1], vo);
      }
      ctx.fill();

      ctx.strokeStyle = `rgba(${EXPLORED_RGB},0.6)`;
      ctx.lineWidth = 1.5;
      ctx.stroke();
    }

    if (showGrid && gridPath.length > 0) {
      ctx.globalCompositeOperation = 'source-over';
      ctx.strokeStyle = GRID_COLOR;
      ctx.lineWidth = 0.5;
      ctx.beginPath();
      for (let i = 0; i < gridPath.length; i += 2) {
        drawHex(ctx, gridPath[i], gridPath[i + 1], vo);
      }
      ctx.stroke();
    }

    return tile;
  },
});

const FogOverlay = ({ routes }) => {
  const map = useMap();
  const layerRef = useRef(null);
  const { tiers } = useMemo(() => buildExploredData(routes), [routes]);

  useEffect(() => {
    if (!map.getPane('fogPane')) {
      map.createPane('fogPane');
      map.getPane('fogPane').style.zIndex = 350;
      map.getPane('fogPane').style.pointerEvents = 'none';
    }

    const layer = new FogGridLayer({ pane: 'fogPane', noWrap: true });
    layer.addTo(map);
    layerRef.current = layer;

    return () => {
      map.removeLayer(layer);
      layerRef.current = null;
    };
  }, [map]);

  useEffect(() => {
    if (layerRef.current) {
      layerRef.current.setTiers(tiers);
    }
  }, [tiers]);

  return null;
};

export default FogOverlay;
